import type {
    CommerceCheckout,
    CompleteCommerceCheckoutResponse,
    CommerceEventUnsubscribe,
    CommercePaymentAdapter,
    CommercePaymentChangedEvent,
    CommercePaymentChangedListener,
    CommercePaymentDomain,
    CommercePaymentResult,
    CommercePaymentState,
    SubmitCommerceCheckoutResponse,
    SubmitCommercePaymentOrderRequest,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceError } from "../errors/commerce-error.js";

export class CommercePaymentService implements CommercePaymentDomain {
    private readonly listeners = new DisposableListenerRegistry<CommercePaymentChangedEvent>();
    private adapter: CommercePaymentAdapter | undefined;
    private currentState: CommercePaymentState = createState("unconfigured", null, 0, null, null);
    private activeSubmission: Promise<CommercePaymentResult> | undefined;
    private pendingCompletion:
        { readonly attemptId: string; readonly paymentResult: unknown } | undefined;

    public constructor(
        private readonly getCheckout: () => CommerceCheckout | undefined,
        private readonly preparePayment: (
            consents: readonly import("@teqtank/commerce-contracts").CommerceCustomerConsentSelection[],
            customFields: Readonly<Record<string, string>>,
        ) => Promise<SubmitCommerceCheckoutResponse>,
        private readonly completePayment: (
            attemptId: string,
            paymentResult: unknown,
        ) => Promise<CompleteCommerceCheckoutResponse>,
        private readonly assertAvailable: () => void,
        adapter?: CommercePaymentAdapter,
    ) {
        if (adapter !== undefined) this.configure(adapter);
    }

    public get state(): CommercePaymentState {
        return this.currentState;
    }

    public configure(adapter: CommercePaymentAdapter): CommercePaymentState {
        this.assertAvailable();
        validateAdapter(adapter);
        if (this.adapter !== adapter) this.adapter?.dispose();
        this.adapter = adapter;
        return this.transition(
            "configured",
            createState("ready", adapter.name, this.currentState.attempt, null, null),
        );
    }

    public setBilling(address: import("@teqtank/commerce-contracts").CommerceAddress | null): void {
        if (address === null) this.adapter?.clearBilling();
        else this.adapter?.setBilling(address);
    }

    public submitOrder(
        request: SubmitCommercePaymentOrderRequest = {},
    ): Promise<CommercePaymentResult> {
        if (this.activeSubmission !== undefined) return this.activeSubmission;
        this.activeSubmission = this.submitOrderCore(request).finally(() => {
            this.activeSubmission = undefined;
        });
        return this.activeSubmission;
    }

    private async submitOrderCore(
        request: SubmitCommercePaymentOrderRequest,
    ): Promise<CommercePaymentResult> {
        this.assertAvailable();
        const adapter = this.adapter;
        if (adapter === undefined)
            throw new CommerceError(
                "payment-not-configured",
                "Payment has not been configured for this Commerce runtime.",
            );
        const checkout = this.getCheckout();
        if (checkout === undefined)
            throw new CommerceError("checkout-not-found", "Checkout has not been started.");
        if (!checkout.validation.isValid)
            throw new CommerceError(
                "checkout-operation-failed",
                "Checkout must be valid before payment can begin.",
            );
        const attempt = this.currentState.attempt + 1;
        this.transition("started", createState("processing", adapter.name, attempt, null, null));
        const billingAddress = Object.prototype.hasOwnProperty.call(request, "billingAddress")
            ? request.billingAddress
            : checkout.billingAddress;
        if (billingAddress != null) adapter.setBilling(billingAddress);
        else adapter.clearBilling();
        try {
            let completion: CompleteCommerceCheckoutResponse;
            let providerResponse: unknown;
            if (this.pendingCompletion !== undefined) {
                providerResponse = this.pendingCompletion.paymentResult;
                completion = await this.completePayment(
                    this.pendingCompletion.attemptId,
                    providerResponse,
                );
            } else {
                const prepared = await this.preparePayment(
                    request.consents ?? [],
                    request.customFields ?? {},
                );
                const response = await adapter.createOrder(prepared.paymentOrder);
                providerResponse = response.response;
                this.pendingCompletion = Object.freeze({
                    attemptId: prepared.attemptId,
                    paymentResult: providerResponse,
                });
                completion = await this.completePayment(prepared.attemptId, providerResponse);
            }
            this.pendingCompletion = undefined;
            const result: CommercePaymentResult = Object.freeze({
                status: "succeeded",
                checkoutId: checkout.id,
                response: providerResponse,
                order: completion.order,
                redirectUrl: completion.redirectUrl,
                warnings: Object.freeze([...completion.warnings]),
                completedAt: new Date(),
            });
            this.transition(
                "succeeded",
                createState("succeeded", adapter.name, attempt, null, result),
            );
            return result;
        } catch (error) {
            const message = describePaymentError(error);
            this.transition("failed", createState("failed", adapter.name, attempt, message, null));
            if (error instanceof CommerceError) throw error;
            throw new CommerceError("payment-operation-failed", message, {
                cause: error,
            });
        }
    }

    public retry(request: SubmitCommercePaymentOrderRequest = {}): Promise<CommercePaymentResult> {
        return this.submitOrder(request);
    }

    public cancel(): CommercePaymentState {
        this.assertAvailable();
        this.adapter?.unsetPaymentWindow?.();
        return this.transition(
            "cancelled",
            createState(
                "cancelled",
                this.adapter?.name ?? null,
                this.currentState.attempt,
                null,
                null,
            ),
        );
    }

    public onChanged(listener: CommercePaymentChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }

    public dispose(): void {
        if (this.currentState.status === "disposed") return;
        this.adapter?.dispose();
        this.adapter = undefined;
        this.transition(
            "disposed",
            createState("disposed", null, this.currentState.attempt, null, null),
        );
        this.listeners.release();
    }

    private transition(
        reason: CommercePaymentChangedEvent["reason"],
        state: CommercePaymentState,
    ): CommercePaymentState {
        this.currentState = state;
        this.listeners.emit(Object.freeze({ reason, state }));
        return state;
    }
}

function createState(
    status: CommercePaymentState["status"],
    adapterName: string | null,
    attempt: number,
    error: string | null,
    result: CommercePaymentResult | null,
): CommercePaymentState {
    return Object.freeze({ status, adapterName, attempt, error, result });
}

function validateAdapter(adapter: CommercePaymentAdapter): void {
    if (
        typeof adapter !== "object" ||
        adapter === null ||
        typeof adapter.name !== "string" ||
        adapter.name.trim().length === 0 ||
        typeof adapter.createOrder !== "function" ||
        typeof adapter.setBilling !== "function" ||
        typeof adapter.clearBilling !== "function" ||
        typeof adapter.dispose !== "function"
    ) {
        throw new CommerceError(
            "configuration-invalid",
            "Payment adapter must implement CommercePaymentAdapter.",
            { property: "paymentAdapter" },
        );
    }
}

function describePaymentError(error: unknown): string {
    if (error instanceof Error && error.message.trim().length > 0) return error.message;
    if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof error.response === "string"
    )
        return error.response;
    return "Payment could not be completed.";
}
